import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Blueprint, BlueprintParseResult, BlueprintState } from "types/generated";
import { INITIAL_TEMPLATE } from "views/Account/Blueprints/constants";
import { showCreateBlueprintDrawer } from "views/Account/Blueprints/CreateBlueprintDrawer";
import { showCreateStackDrawer } from "views/Account/Blueprints/CreateStackDrawer";
import BlueprintsFeatureGateTooltip from "views/Account/Blueprints/FeatureGate/Tooltip";
import { BlueprintActions } from "views/Account/Blueprints/FeatureGate/types";
import { UPDATE_BLUEPRINT } from "views/Account/Blueprints/gql";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TemplateFeatureGateTooltip from "views/Account/TemplatesWorkbench/FeatureGate/Tooltip";
import { TemplateActions } from "views/Account/TemplatesWorkbench/FeatureGate/types";

import BlueprintShareButton from "../components/ShareButton";
import BlueprintEditor from "../Editor";
import { VALIDATE_BLUEPRINT_TEMPLATE } from "../gql";
import { showPublishConfirmationModal } from "../PublishConfirmationModal";
import { showExportToTemplateModal } from "../components/ExportToTemplate";
import { GET_BLUEPRINT_MIGRATED_TEMPLATE } from "../components/ExportToTemplate/gql";
import styles from "./styles.module.css";
import useBlueprintTemplateBodyAnalytics from "./useBlueprintTemplateBodyAnalytics";

type BlueprintTemplateBodyProps = {
  blueprint: Blueprint;
  blueprintSchema: string;
};

const BlueprintTemplateBody = ({ blueprint, blueprintSchema }: BlueprintTemplateBodyProps) => {
  const [editorBody, setEditorBody] = useState<string>(INITIAL_TEMPLATE);
  const { reportSuccess, onError } = useTypedContext(FlashContext);
  const [errors, setErrors] = useState<string[]>([]);
  const [fetchMigratedTemplate] = useLazyQuery(GET_BLUEPRINT_MIGRATED_TEMPLATE);
  const trackAnalytics = useBlueprintTemplateBodyAnalytics();

  const [validateTemplate] = useMutation<{
    blueprintParseTemplate: BlueprintParseResult;
  }>(VALIDATE_BLUEPRINT_TEMPLATE);

  const editorHasUnsavedChanges = blueprint.rawTemplate
    ? editorBody !== blueprint.rawTemplate
    : editorBody !== INITIAL_TEMPLATE;
  const isBodyEmpty = !editorBody.trim();

  useBlockNavigationModal({
    title: "Leave confirmation",
    description: "Are you sure you want to leave? You have unsubmitted changes.",
    isNavigationBlocked: editorHasUnsavedChanges,
  });

  const [updateBlueprint, { loading: saveLoading }] = useMutation<{ blueprintUpdate: Blueprint }>(
    UPDATE_BLUEPRINT
  );

  const handleUpdateBlueprint = (publish?: boolean) => {
    if (blueprint) {
      updateBlueprint({
        variables: {
          id: blueprint.id,
          input: {
            name: blueprint.name,
            description: blueprint.description,
            labels: blueprint.labels,
            space: blueprint.space.id,
            state: publish ? BlueprintState.Published : blueprint.state,
            template: editorBody,
          },
        },
      })
        .then(({ data }) => {
          if (data?.blueprintUpdate?.name) {
            reportSuccess({
              message: `Blueprint "${data.blueprintUpdate.name}" is successfully saved`,
            });
          }
        })
        .catch(onError);
    }
  };

  const handleExportToTemplate = async () => {
    trackAnalytics("Export to template clicked");

    try {
      const { data: templateData } = await fetchMigratedTemplate({
        variables: { id: blueprint.id },
      });

      const migratedTemplate = templateData?.blueprint?.template?.template;
      const warnings = templateData?.blueprint?.template?.warnings ?? [];
      const migrationErrors = templateData?.blueprint?.template?.errors ?? [];

      if (migratedTemplate) {
        showExportToTemplateModal({
          blueprintName: blueprint.name,
          blueprintDescription: blueprint.description,
          blueprintSpaceId: blueprint.space.id,
          blueprintLabels: blueprint.labels ?? [],
          originalTemplate: blueprint.rawTemplate ?? "",
          migratedTemplate,
          warnings,
          errors: migrationErrors,
        });
      }
    } catch (e) {
      onError(e);
    }
  };

  const handleValidateTemplate = useCallback(
    (template: string) => {
      setErrors([]);

      return validateTemplate({
        variables: {
          template,
        },
      })
        .then(({ data }) => {
          setErrors(data?.blueprintParseTemplate?.errors || []);

          return {
            inputs: data?.blueprintParseTemplate?.inputs || [],
            errors: data?.blueprintParseTemplate?.errors || [],
          };
        })
        .catch(onError);
    },
    [onError, validateTemplate]
  );

  const handleSaveTemplate = useCallback(async () => {
    await handleUpdateBlueprint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorBody, blueprint]);

  const handleOpenCreateStackDrawer = () => {
    showCreateStackDrawer({
      item: blueprint,
      onEditMetadata: handleOpenEditDrawer,
    });
  };

  const handleEditorChange = (value?: string) => {
    setEditorBody(value || "");
  };

  const handleCancel = () => {
    const savedBody = blueprint.rawTemplate || INITIAL_TEMPLATE;
    setEditorBody(savedBody);
    handleValidateTemplate(savedBody);
  };

  const handleOpenEditDrawer = () => {
    showCreateBlueprintDrawer({ blueprint: blueprint });
  };

  useEffect(() => {
    if (blueprint?.rawTemplate) {
      setEditorBody(blueprint.rawTemplate);
      handleValidateTemplate(blueprint.rawTemplate);
    } else {
      handleValidateTemplate(editorBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprint?.rawTemplate]);

  useTitle(`Template body · ${blueprint?.name}`);

  const isPublished = blueprint.state === BlueprintState.Published;

  const onPublishConfirmation = () => {
    showPublishConfirmationModal({
      onPublish: () => handleUpdateBlueprint(true),
      blueprint,
    });
  };

  return (
    <>
      <Box direction="column" grow="1" fullWidth relative>
        <PageInfoHeader title="Template body">
          {isPublished && blueprint?.id && (
            <BlueprintShareButton blueprintId={blueprint.id} size="medium" variant="secondary" />
          )}
          {isPublished && (
            <TemplateFeatureGateTooltip
              action={TemplateActions.CreateTemplate}
              on={({ isDisabled, ...props }) => (
                <Button
                  {...props}
                  variant="secondary"
                  size="medium"
                  onPress={handleExportToTemplate}
                  disabled={isDisabled}
                >
                  Export to template
                </Button>
              )}
            />
          )}
          {isPublished && (
            <BlueprintsFeatureGateTooltip
              action={BlueprintActions.CreateStack}
              on={({ isDisabled, ...props }) => (
                <Button
                  {...props}
                  variant="primary"
                  size="medium"
                  onPress={handleOpenCreateStackDrawer}
                  disabled={isDisabled}
                >
                  Create stack
                </Button>
              )}
            />
          )}

          {!isPublished && (
            <>
              {editorHasUnsavedChanges && (
                <Button
                  variant="secondary"
                  size="medium"
                  onPress={handleCancel}
                  disabled={saveLoading}
                >
                  Cancel
                </Button>
              )}

              <BlueprintsFeatureGateTooltip
                action={BlueprintActions.Edit}
                blueprint={blueprint}
                on={({ isDisabled, ...props }) => (
                  <Button
                    {...props}
                    variant="secondary"
                    size="medium"
                    onPress={handleSaveTemplate}
                    disabled={isDisabled}
                    ariaDisabled={!editorHasUnsavedChanges || isBodyEmpty}
                    loading={saveLoading}
                  >
                    Save
                  </Button>
                )}
              />

              <BlueprintsFeatureGateTooltip
                action={BlueprintActions.Publish}
                blueprint={blueprint}
                on={({ isDisabled, ...props }) => (
                  <Button
                    {...props}
                    variant="primary"
                    size="medium"
                    onPress={onPublishConfirmation}
                    disabled={isDisabled || errors.length > 0}
                    ariaDisabled={isBodyEmpty}
                  >
                    Publish
                  </Button>
                )}
              />
            </>
          )}
        </PageInfoHeader>

        <Box direction="column" grow="1" fullWidth relative className={styles.editorWrapper}>
          <BlueprintEditor
            blueprint={blueprint}
            validate={handleValidateTemplate}
            body={editorBody}
            schema={blueprintSchema}
            onChange={handleEditorChange}
            readOnly={isPublished || blueprint.deleted}
          />
        </Box>
      </Box>
    </>
  );
};

export default BlueprintTemplateBody;
